/**
 * PackageView — the assembled deployable (R3 `TaskSnapshot.package`).
 *
 * MEMBERS ↔ WORK ITEMS: a `PackageMemberView` and the `WorkItemView` that produced it share no id —
 * only `metadata_type` + `full_name` — so `matchMemberToWorkItem` (views.ts) joins them on that pair.
 * Callers that already have the build's items indexed (e.g. `LaneV2TaskPanel`, which renders both
 * `BuildProgressView` and this) can pass `itemsByKey` straight through instead of paying to rebuild
 * the index; passing `build` alone still works, it just builds the index once here.
 *
 * "N excluded" is a disclosure, not a dropped list — an excluded member is the OTHER half of the same
 * event a `dropped` work item shows in `BuildProgressView` (the garp fixture: `Report` deleted item →
 * `error_code: "unsupported_type"` at build → the same component in `package.excluded` with
 * `reason: "unsupported_type"`), so it uses the identical reason vocabulary rather than re-deriving one.
 */
import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Copy, GitBranch, Info, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { laneV2IdeState } from "@/services/multi-agent/ideState";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import type { SaveBranchSettled } from "@/services/multi-agent/laneV2/branchSave";
import { branchConflictOf, branchSaveRejectionOf, describeLaneV2Error, isLaneV2Error } from "@/services/multi-agent/laneV2/api";
import {
  BranchSaveGoneError,
  BranchSaveInterruptedError,
  isAbortError,
} from "@/services/multi-agent/laneV2/branchSaveRun";
import type { SaveBranchRunOpts } from "@/stores/laneV2SessionStore";
import type {
  BranchSaveRecord,
  BranchSaveRunningView,
  BuildView,
  PackageView as PackageViewData,
  SaveBranchIn,
  SaveBranchFileResult,
  SaveBranchOut,
  WorkItemView,
} from "@/services/multi-agent/laneV2/types";
import {
  branchConflictFileRow,
  buildItemsByKey,
  defaultBranchSaveMembers,
  isFailedOrHeldMember,
  saveNotTickedReason,
  formatLocalTimestamp,
  groupExcludedByReason,
  isSkippedSaveFile,
  isValidBranchName,
  matchMemberToWorkItem,
  outOfScopeReasonLabel,
  packageMemberComponentKey,
  saveSkipReasonLabel,
  SAVE_HAS_CONFLICTS_NOTE,
  HELD_BACK_LABEL,
  isHeldBack,
  isInformationalReason,
  isNotWritten,
  NOT_WRITTEN_LABEL,
  NOT_WRITTEN_REASON,
  shortSha,
  hasReasonLabel,
  validationReasonLabel,
  validationStatusLabel,
} from "./views";

export interface PackageViewProps {
  pkg: PackageViewData;
  build?: BuildView | null;
  itemsByKey?: ReadonlyMap<string, WorkItemView>;
  onViewItemBody?: (workItemId: string) => void;
  className?: string;
}

/** The reasons a member carries as raw codes, with the "nothing was written" sentence first for a not-changed member. */
function memberNotes(reasons: string[] | null | undefined, notWritten: boolean): string[] {
  const all = (reasons ?? []).filter((r) => validationReasonLabel(r).trim() !== "" && !isInformationalReason(r));
  if (!notWritten) return all;
  // That member's `no_body_written` reason is folded into the one standard sentence, not a second line.
  return [NOT_WRITTEN_REASON, ...all.filter((r) => !isNotWritten({ validation_reasons: [r] }))];
}

/** What a member's status reads as, whatever its wire label (held back / not changed win over the label). */
function memberShown(status: string | null | undefined, heldBack: boolean, notWritten: boolean): string | null {
  if (notWritten) return "not_written";
  if (heldBack) return "held_back";
  return status ? status : null;
}

function memberTone(shown: string): string {
  return shown === "validation_failed" ? "text-ma-err-text" : shown === "validated" ? "text-muted-foreground" : "text-ma-warn-text";
}

/** The raw reason on hover when the row shows humanised words rather than one of this screen's own sentences (so the code is never hidden). */
function reasonTitle(reason: string): string | undefined {
  const label = validationReasonLabel(reason);
  return hasReasonLabel(reason) || label === reason ? undefined : reason;
}

type MemberValidationProps = { status?: string | null; reasons?: string[] | null; heldBack?: boolean; notWritten?: boolean };

/** How many notes a member carries (what the info button counts). */
export function memberNoteCount({ reasons, notWritten = false }: MemberValidationProps): number {
  return memberNotes(reasons, notWritten).length;
}

/** One row's panel toggled; the others are untouched (several rows can be open at once). */
export function toggleNoteKey(open: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(open);
  if (!next.delete(key)) next.add(key);
  return next;
}

/** "Show all notes" / "Hide all notes": every row with notes opens, or every panel closes when they all were open already. */
export function toggleAllNotes(open: ReadonlySet<string>, keys: string[]): ReadonlySet<string> {
  const allOpen = keys.length > 0 && keys.every((k) => open.has(k));
  return allOpen ? new Set() : new Set(keys);
}

/** The set of open note panels, keyed by row. */
export function useOpenNotes() {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  return {
    open,
    toggle: (key: string) => setOpen((prev) => toggleNoteKey(prev, key)),
    toggleAll: (keys: string[]) => setOpen((prev) => toggleAllNotes(prev, keys)),
  };
}

/** The notes behind a member's status, expanded in place under its row: full width, wrapping fully, one list item per note
 *  (server sentence wins; an unmapped code is humanised, the raw code on the item's `title`). */
export function MemberNotesPanel({ id, notes, className }: { id?: string; notes: string[]; className?: string }) {
  return (
    <div id={id} data-member-notes-panel="" className={cn("rounded border border-border/60 bg-muted/30 px-2.5 py-1.5 text-[12px]", className)}>
      <div className="text-[10px] font-medium text-muted-foreground">{notes.length} {notes.length === 1 ? "note" : "notes"}</div>
      <ul className="mt-1 list-none space-y-1 break-words [overflow-wrap:anywhere]" data-member-notes="">
        {notes.map((r, i) => (
          <li key={i} title={reasonTitle(r)} className="text-foreground">{validationReasonLabel(r)}</li>
        ))}
      </ul>
    </div>
  );
}

/** The panel for one member, or nothing when it has no notes. */
export function MemberNotes({ id, reasons, notWritten = false, className }: { id?: string; reasons?: string[] | null; notWritten?: boolean; className?: string }) {
  const notes = memberNotes(reasons, notWritten);
  return notes.length === 0 ? null : <MemberNotesPanel id={id} notes={notes} className={className} />;
}

/** The inline half of a member's validation: the status as a small raised word right after the name. With notes, the word and a
 *  small icon-only info mark form one button that toggles the member's notes panel (click or tap, not hover).
 *  Put it OUTSIDE any truncating name span (`shrink-0`). */
export function MemberStatusMark({ status, reasons, heldBack = false, notWritten = false, open = false, onToggle, panelId }: MemberValidationProps & { open?: boolean; onToggle?: () => void; panelId?: string }) {
  const shown = memberShown(status, heldBack, notWritten);
  const count = memberNotes(reasons, notWritten).length;
  if (!shown && count === 0) return null;
  const label = notWritten ? NOT_WRITTEN_LABEL : heldBack ? HELD_BACK_LABEL : status ? validationStatusLabel(status) : null;
  const tone = memberTone(shown ?? "");
  const word = shown && label && (
    <sup className={cn("text-[10px] font-medium leading-none", tone)} data-member-validation={shown}>
      {label}
    </sup>
  );
  return (
    <span className="ml-1 shrink-0 whitespace-nowrap">
      {count === 0 ? (
        word
      ) : (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={`${open ? "Hide" : "Show"} ${count} ${count === 1 ? "note" : "notes"}`}
          data-member-reasons={count}
          className={cn("inline-flex items-start gap-0.5 rounded-sm leading-none hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", tone)}
        >
          {word}
          <Info aria-hidden className="h-[11px] w-[11px] align-super" />
        </button>
      )}
    </span>
  );
}

/** Rule 23: a FAILED member is "the news" - its first reason stays on the row as one truncated line (full text on hover).
 *  Everything else (not checked, not changed, held back) shows only its status word; its notes are one click away. */
export function MemberFailureLine({ status, reasons, notWritten = false }: MemberValidationProps) {
  if (status !== "validation_failed") return null;
  const first = memberNotes(reasons, notWritten)[0];
  if (!first) return null;
  const text = validationReasonLabel(first);
  return (
    <p className="mt-0.5 max-w-full truncate text-[11px] text-muted-foreground" title={text} data-member-failure="">
      {text}
    </p>
  );
}

export function PackageView({ pkg, build, itemsByKey, onViewItemBody, className }: PackageViewProps) {
  const [copied, setCopied] = useState(false);
  const lastSave = pkg.branch_saves?.[0] ?? null;
  const index = itemsByKey ?? (build ? buildItemsByKey(build.items) : new Map<string, WorkItemView>());
  const excludedGroups = groupExcludedByReason(pkg.excluded ?? []);
  const notesOpen = useOpenNotes();
  const notesPrefix = useId();

  const copyHash = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(pkg.package_hash).catch(() => undefined);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <section aria-labelledby="lanev2-package-title" className={cn("rounded-md border border-border bg-card", className)}>
      <header className="px-3.5 py-2.5">
        <h3 id="lanev2-package-title" className="text-[13px] font-semibold text-foreground">
          Your change
        </h3>
      </header>

      <div className="border-t border-border">
        <table className="w-full text-left text-[12px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="px-3.5 py-1.5 font-medium">Change</th>
              <th className="px-3.5 py-1.5 font-medium">Type</th>
              <th className="px-3.5 py-1.5 font-medium">Name</th>
              <th className="px-3.5 py-1.5 font-medium" />
            </tr>
          </thead>
          <tbody>
            {pkg.members.map((m, i) => {
              const workItem = matchMemberToWorkItem(m, index);
              const noteKey = String(i);
              const panelId = `${notesPrefix}-notes-${i}`;
              const noteCount = memberNoteCount({ reasons: m.validation_reasons, notWritten: isNotWritten(m) });
              const isOpen = noteCount > 0 && notesOpen.open.has(noteKey);
              return (
                <Fragment key={`${m.metadata_type}-${m.full_name}-${i}`}>
                <tr className="border-t border-border/60">
                  <td className="px-3.5 py-1.5 text-muted-foreground">{m.op}</td>
                  <td className="px-3.5 py-1.5 font-mono">{m.metadata_type}</td>
                  <td className="w-full max-w-0 px-3.5 py-1.5">
                    <span className="font-mono">{m.full_name}</span>
                    <MemberStatusMark status={m.validation_status} reasons={m.validation_reasons} heldBack={isHeldBack(m)} notWritten={isNotWritten(m)} open={isOpen} onToggle={() => notesOpen.toggle(noteKey)} panelId={panelId} />
                    <MemberFailureLine status={m.validation_status} reasons={m.validation_reasons} notWritten={isNotWritten(m)} />
                  </td>
                  <td className="px-3.5 py-1.5 text-right">
                    {workItem?.has_body && onViewItemBody && (
                      <button
                        type="button"
                        onClick={() => onViewItemBody(workItem.work_item_id)}
                        className="rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                      >
                        View
                      </button>
                    )}
                  </td>
                </tr>
                {/* Notes open in place: a second row whose panel starts at the Name column (two spacer cells), full remaining width. */}
                {isOpen && (
                  <tr>
                    <td colSpan={2} />
                    <td colSpan={2} className="px-3.5 pb-2 pt-0">
                      <MemberNotes id={panelId} reasons={m.validation_reasons} notWritten={isNotWritten(m)} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {pkg.members.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3.5 py-2.5 text-muted-foreground">
                  No members in this package.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pkg.excluded && pkg.excluded.length > 0 && (
        <ExcludedDisclosure groups={excludedGroups} total={pkg.excluded.length} />
      )}

      {pkg.manual_prerequisites && pkg.manual_prerequisites.length > 0 && (
        <div className="border-t border-border px-3.5 py-2.5">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Needs to exist in the org first</h4>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            The org must already have these; this change does not create them.
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-[12px] text-foreground">
            {pkg.manual_prerequisites.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {/* STATUS ONLY. Save and Validate live on the chat's outcome card (one owner, one state); this just says where things stand. */}
      <p data-lane-v2-package-status={lastSave ? "saved" : "unsaved"} className="border-t border-border px-3.5 py-2.5 text-[12px] text-muted-foreground">
        {lastSave ? (
          <>
            Saved v{lastSave.version} to <span className="font-mono text-foreground">{lastSave.branch}</span> ({lastSave.files_count}{" "}
            {lastSave.files_count === 1 ? "file" : "files"}, {formatLocalTimestamp(lastSave.at)})
          </>
        ) : (
          "Not saved to a branch yet"
        )}
      </p>

      {pkg.branch_saves && pkg.branch_saves.length > 0 && <BranchSaveHistory saves={pkg.branch_saves} />}

      <details data-lane-v2-package-technical="" className="border-t border-border px-3.5 py-2.5">
        <summary className="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">Technical details</summary>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span>Package</span>
          <button
            type="button"
            onClick={copyHash}
            title={pkg.package_hash}
            className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-muted/50 hover:text-foreground"
          >
            <Copy className="h-3 w-3 shrink-0" aria-hidden />
            {shortSha(pkg.package_hash)}
            {copied ? " — copied" : ""}
          </button>
        </div>
      </details>
    </section>
  );
}

function BranchSaveHistory({ saves }: { saves: readonly BranchSaveRecord[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-lane-v2-branch-save-history="" className="border-t border-border px-3.5 py-2.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" aria-hidden /> : <ChevronRight className="h-3 w-3" aria-hidden />}
        <GitBranch className="h-3 w-3 shrink-0" aria-hidden />
        {saves.length} previous branch save{saves.length === 1 ? "" : "s"}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-0.5">
          {saves.map((s, i) => (
            <li key={i} className="text-[11px] text-muted-foreground">
              v{s.version} to <span className="font-mono text-foreground">{s.branch}</span>: {s.files_count} file
              {s.files_count === 1 ? "" : "s"}, {formatLocalTimestamp(s.at)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── save to branch (leg v2-save-branch) ─────────────────────────────────────────────────────────────

export type SaveBranchFailure =
  | { kind: "conflict"; body: ReturnType<typeof branchConflictOf> }
  | { kind: "rejected"; body: ReturnType<typeof branchSaveRejectionOf> }
  | { kind: "replayed" }
  /** A background save (0.2.35) stopped before finishing; rows already on the branch may be partial. */
  | { kind: "interrupted" }
  /** The poll route no longer knows the save (404): its outcome cannot be read any more. */
  | { kind: "gone" }
  /** `BRANCH_SAVE_BUSY` after the one automatic Retry-After retry: offer a manual retry. */
  | { kind: "busy" }
  /** `BRANCH_SAVE_IN_PROGRESS`: another save holds the slot. What it is (async/sync/gone) is R3's `branch_save_running`, not this. */
  | { kind: "in_progress" }
  /** `BRANCH_SAVE_PACKAGE_MOVED` (arrives inside a failed poll's result): the package changed under the save. */
  | { kind: "package_moved" }
  | { kind: "other"; message: string };

/** The exact `SaveBranchIn` body a submit sends — kept pure and exported (like `classifySaveBranchError`
 *  below) so the request shape, in particular that `package_hash` rides along as the bare 64-hex sha256
 *  `pkg.package_hash` already is (never a `"sha256:..."`-prefixed value — that prefix is illustrative
 *  prose in the spec's own example JSON, not the wire shape), is unit-tested without mounting the panel
 *  or clicking through its (collapsed-by-default) disclosure. */
export function buildSaveBranchRequest(branch: string, selected: ReadonlySet<string>, packageHash: string): SaveBranchIn {
  return { branch: branch.trim(), component_keys: Array.from(selected), package_hash: packageHash };
}

/** Classifies a thrown `saveBranch` error into what the panel renders — kept pure and exported so it
 *  can be unit-tested without mounting anything. */
export function classifySaveBranchError(err: unknown): SaveBranchFailure {
  if (err instanceof BranchSaveInterruptedError) return { kind: "interrupted" };
  if (err instanceof BranchSaveGoneError) return { kind: "gone" };
  if (isLaneV2Error(err)) {
    if (err.code === "BRANCH_SAVE_BUSY") return { kind: "busy" };
    if (err.code === "BRANCH_SAVE_IN_PROGRESS") return { kind: "in_progress" };
    if (err.code === "BRANCH_SAVE_PACKAGE_MOVED") return { kind: "package_moved" };
    if (err.code === "BRANCH_CONFLICT") return { kind: "conflict", body: branchConflictOf(err) };
    if (err.code === "BRANCH_SAVE_REJECTED") return { kind: "rejected", body: branchSaveRejectionOf(err) };
    if (err.code === "BRANCH_SAVE_REPLAYED") return { kind: "replayed" };
    return { kind: "other", message: describeLaneV2Error(err) };
  }
  return { kind: "other", message: err instanceof Error ? err.message : "Save failed." };
}

/** Fallback workspace branch when the IDE has none selected. */
export const FALLBACK_SAVE_BRANCH = "agent-edit";

/**
 * Where Save goes by default: the IDE's working git branch, the same source chat "save it" reads
 * (`laneV2IdeState().git_branch`), else `agent-edit`. Still editable in the form.
 */
export function defaultSaveBranch(): string {
  const branch = (laneV2IdeState()?.git_branch ?? "").trim();
  return branch && isValidBranchName(branch) ? branch : FALLBACK_SAVE_BRANCH;
}

/** The submit button's words: names the branch it will write to. */
export function saveSubmitLabel(branch: string): string {
  const name = branch.trim();
  return name ? `Save as workspace branch ${name}` : "Save as workspace branch";
}

/** Rows an earlier chunk had already written when a conflict hit (0.2.35 `written`). */
export function savedFilesCount(body: { written?: readonly unknown[] | null } | null | undefined): number {
  return Array.isArray(body?.written) ? body.written.length : 0;
}

/** A lost answer with a newer history row: hedged, never a success card (a save's row is partial while it runs). */
export function wroteSomeLine(r: BranchSaveRecord): string {
  return `The answer was lost. At least ${r.files_count} file${r.files_count === 1 ? "" : "s"} reached ${r.branch} (v${r.version}). Check the branch before saving again.`;
}

/** One line for the outcome card (outside the panel) when a save ended in failure. */
export function saveFailureLine(failure: SaveBranchFailure): string {
  switch (failure.kind) {
    case "interrupted": return "The save was interrupted. Files already written stay written.";
    case "gone": return "A save's outcome could not be read. Check the branch.";
    case "package_moved": return "The change was updated while saving. Save again.";
    case "conflict": return savedFilesCount(failure.body) > 0 ? `The branch moved; ${savedFilesCount(failure.body)} files were saved before the conflict.` : "The branch moved. Nothing was saved.";
    case "busy": return "Saves are busy right now. Nothing was saved.";
    case "rejected": return "Your workspace branch rejected this save.";
    default: return "The save did not complete.";
  }
}

/** What a settled save shows. `still_running` shows neither: R3 `branch_save_running` (the `running` prop) renders it. */
export function settledView(out: SaveBranchSettled): { result: SaveBranchOut | null; wroteSome: BranchSaveRecord | null } {
  if (out.kind === "saved") return { result: out.out, wroteSome: null };
  if (out.kind === "wrote_some") return { result: null, wroteSome: out.record };
  return { result: null, wroteSome: null };
}

/** The line shown while R3 says a save runs: async → chunks; sync → the chat seat or a small save, no count to show. */
export function runningSaveLine(running: BranchSaveRunningView, chunks: number): string {
  return running.mode === "async" ? saveProgressLabel(chunks) : "A save is running; it finishes shortly.";
}

/** Rows a chunked save had already written (409 `written[]`). */
function SavedRows({ files }: { files: readonly SaveBranchFileResult[] }) {
  return (
    <ul data-lane-v2-save-branch-written-rows="" className="mt-1 space-y-0.5 text-muted-foreground">
      {files.map((f, i) => (
        <li key={i} className="font-mono">
          {f.path}
          <span className="ml-1.5 font-sans">{f.status}{f.result ? ` (${f.result})` : ""}</span>
        </li>
      ))}
    </ul>
  );
}

/** The in-progress line of a background save. */
export function saveProgressLabel(chunksWritten: number): string {
  return `Saving to branch… (${chunksWritten} chunk${chunksWritten === 1 ? "" : "s"} written)`;
}

export function SaveToBranchPanel({
  members,
  packageHash,
  onSaveBranch,
  taskId,
  running = null,
}: {
  members: PackageViewData["members"];
  packageHash: string;
  onSaveBranch: (body: SaveBranchIn, opts?: SaveBranchRunOpts) => Promise<SaveBranchSettled>;
  /** For reading the store's tracked save (a resumed poll's chunk count / failure). Omitted in isolation. */
  taskId?: string;
  /** R3 `package.branch_save_running`: the SERVER's word that a save is running (this tab's, another tab's, or the chat seat's). */
  running?: BranchSaveRunningView | null;
}) {
  const tracked = useLaneV2SessionStore((st) => (taskId ? st.saves[taskId] : undefined));
  const [branch, setBranch] = useState(defaultSaveBranch);
  const defaultSelection = useMemo(() => new Set(defaultBranchSaveMembers(members)), [members]);
  const [selected, setSelected] = useState<Set<string>>(defaultSelection);
  const [saving, setSaving] = useState(false);
  /** `chunks_written` of a background save; `null` while the save answered synchronously (or none runs). */
  const [progress, setProgress] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Leaving (unmount / session change remounts the panel) stops polling; the save itself continues server-side.
  useEffect(() => () => abortRef.current?.abort(), []);
  const [result, setResult] = useState<SaveBranchOut | null>(null);
  const [wroteSome, setWroteSome] = useState<BranchSaveRecord | null>(null);
  const [failure, setFailure] = useState<SaveBranchFailure | null>(null);

  const branchValid = isValidBranchName(branch);
  const selectedCount = selected.size;
  const notTickedCount = members.filter((m) => !selected.has(packageMemberComponentKey(m)) && isFailedOrHeldMember(m)).length;

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const doSave = async () => {
    if (saving) return;
    const ctl = new AbortController();
    abortRef.current = ctl;
    setSaving(true);
    setProgress(null);
    setResult(null);
    setWroteSome(null);
    setFailure(null);
    try {
      const out = await onSaveBranch(buildSaveBranchRequest(branch, selected, packageHash), {
        signal: ctl.signal,
        onProgress: (n) => {
          if (!ctl.signal.aborted) setProgress(n);
        },
      });
      if (!ctl.signal.aborted) {
        const view = settledView(out);
        setResult(view.result);
        setWroteSome(view.wroteSome);
      }
    } catch (err) {
      if (ctl.signal.aborted || isAbortError(err)) return;
      setFailure(classifySaveBranchError(err));
    } finally {
      if (!ctl.signal.aborted) {
        setSaving(false);
        setProgress(null);
      }
    }
  };

  return (
    <div data-lane-v2-save-branch-form="">
      <div className="space-y-2.5 border-t border-border/60 px-3.5 py-3">
        <label className="flex items-center gap-2 text-[12px] text-foreground">
          <span className="shrink-0 text-muted-foreground">Workspace branch</span>
          <input
            type="text"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            aria-invalid={!branchValid}
            data-lane-v2-save-branch-input=""
            className={cn(
              "min-w-0 flex-1 basis-32 rounded border bg-transparent px-1.5 py-0.5 font-mono text-[12px] text-foreground focus:outline-none focus:ring-1",
              branchValid ? "border-border focus:ring-[hsl(var(--primary))]" : "border-destructive focus:ring-destructive",
            )}
          />
        </label>
        {!branchValid && (
          <p className="text-[11px] text-ma-err-text">&ldquo;{branch}&rdquo; is not a valid branch name.</p>
        )}

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Files ({selectedCount} of {members.length} selected)
          </p>
          {notTickedCount > 0 && (
            <p className="text-[11px] text-muted-foreground">
              {notTickedCount} not ticked because they failed or are held back. Tick to include them.
            </p>
          )}
          <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto">
            {members.map((m, i) => {
              const key = packageMemberComponentKey(m);
              const reason = saveNotTickedReason(m);
              const reasonId = `save-branch-reason-${i}`;
              return (
                <li key={`${key}-${i}`} className="flex items-center gap-1.5 text-[12px]">
                  <input
                    type="checkbox"
                    checked={selected.has(key)}
                    onChange={() => toggle(key)}
                    aria-label={key}
                    aria-describedby={reason ? reasonId : undefined}
                    data-lane-v2-save-branch-member={key}
                  />
                  <span className="font-mono text-foreground">{key}</span>
                  {reason && (
                    <span id={reasonId} className="text-[10px] text-muted-foreground">
                      {reason}
                    </span>
                  )}
                  {!m.body_sha256 && <span className="text-[10px] text-muted-foreground">no bytes to save</span>}
                </li>
              );
            })}
          </ul>
        </div>

        <button
          type="button"
          onClick={() => void doSave()}
          disabled={saving || running != null || !branchValid || selectedCount === 0}
          data-lane-v2-save-branch-submit=""
          className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--primary))] px-3 py-1.5 text-[13px] font-medium text-[hsl(var(--primary-foreground))] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
          {saveSubmitLabel(branch)}
        </button>

        {running != null ? (
          <p role="status" data-lane-v2-save-branch-progress={running.mode === "async" ? "async" : "sync"} className="text-[11px] text-muted-foreground">
            {runningSaveLine(running, tracked?.phase === "running" ? tracked.chunks : (progress ?? running.chunks_written))}
          </p>
        ) : (
          saving &&
          progress !== null && (
            <p role="status" data-lane-v2-save-branch-progress="async" className="text-[11px] text-muted-foreground">
              {saveProgressLabel(progress)}
            </p>
          )
        )}

        {(failure ?? (tracked?.phase === "failed" ? classifySaveBranchError(tracked.error) : null)) && (
          <SaveBranchFailureBanner
            failure={(failure ?? (tracked?.phase === "failed" ? classifySaveBranchError(tracked.error) : null)) as SaveBranchFailure}
            slotFree={running == null}
            onRetry={() => void doSave()}
          />
        )}

        {wroteSome && (
          <div data-lane-v2-save-branch-wrote-some="" role="status" className="rounded border border-ma-warn-line px-2.5 py-2 text-[11px] text-ma-warn-text">
            {wroteSomeLine(wroteSome)}
          </div>
        )}

        {result && <SaveBranchResultView result={result} />}
      </div>
    </div>
  );
}

export function SaveBranchFailureBanner({ failure, onRetry, slotFree = true }: { failure: SaveBranchFailure; onRetry: () => void; slotFree?: boolean }) {
  if (failure.kind === "conflict") {
    const rows = (failure.body?.files ?? []).map(branchConflictFileRow);
    return (
      <div data-lane-v2-save-branch-conflict="" className="rounded border border-ma-warn-line px-2.5 py-2 text-[11px] text-ma-warn-text">
        <div className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {savedFilesCount(failure.body) > 0 ? "The branch moved since these files were read." : "The branch moved since these files were read. Nothing was saved."}
        </div>
        {savedFilesCount(failure.body) > 0 && (
          <p data-lane-v2-save-branch-written="" className="mt-0.5">
            {savedFilesCount(failure.body)} file{savedFilesCount(failure.body) === 1 ? " was" : "s were"} saved before the conflict.
          </p>
        )}
        {savedFilesCount(failure.body) > 0 && <SavedRows files={failure.body?.written ?? []} />}
        {failure.body?.revision != null && (
          <p className="mt-0.5 text-muted-foreground">It is now at {String(failure.body.revision)}.</p>
        )}
        {rows.length > 0 && (
          <table className="mt-1.5 w-full text-left text-[11px] text-muted-foreground">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide">
                <th className="pr-2 font-medium">Component</th>
                <th className="pr-2 font-medium">Path</th>
                <th className="pr-2 font-medium">Status</th>
                <th className="font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="align-top">
                  <td className="pr-2 pt-0.5 font-mono">{r.component}</td>
                  <td className="pr-2 pt-0.5 font-mono">{r.path}</td>
                  <td className="pr-2 pt-0.5">{r.status}</td>
                  <td className="pt-0.5">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {/*
          NOT actually a "re-read": the save route computes every per-file base_hash from its own
          captured baseline tables fresh on every call, and the conflict this banner reports is about
          the PEER's own branch head moving, not about anything R3's package view carries. A client-side
          refetch of the task snapshot here would not change what gets sent on retry, so this is
          honestly labelled as the plain retry it is — pressing it reserves a new version and posts
          again, which already reads everything fresh server-side.
        */}
        <button
          type="button"
          onClick={onRetry}
          className="mt-1.5 rounded border border-border px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted/50"
        >
          Try again
        </button>
      </div>
    );
  }
  if (failure.kind === "in_progress") {
    // Whether a save really runs (and which kind) is the server's word, `branch_save_running` -> `slotFree`.
    return (
      <div data-lane-v2-save-branch-in-progress="" role="status" className="rounded border border-ma-warn-line px-2.5 py-2 text-[11px] text-ma-warn-text">
        <div className="flex items-center gap-1.5 font-medium">
          {slotFree ? <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden /> : <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />}
          {slotFree ? "The save that was running has finished. Save is available again." : "A save of this change is already running. Its result shows here when it finishes."}
        </div>
      </div>
    );
  }
  if (failure.kind === "package_moved") {
    return (
      <div data-lane-v2-save-branch-incomplete="package_moved" className="rounded border border-ma-warn-line px-2.5 py-2 text-[11px] text-ma-warn-text">
        <div className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          The change was updated while it was saving. Nothing newer was saved. Save again to save the current version.
        </div>
      </div>
    );
  }
  if (failure.kind === "interrupted" || failure.kind === "gone" || failure.kind === "busy") {
    const copy = {
      interrupted: "The save was interrupted. Files already written stay written. Save again.",
      gone: "This save can no longer be found, so its outcome is unknown. Check the branch before saving again.",
      busy: "Saves are busy right now. Nothing was saved.",
    }[failure.kind];
    return (
      <div data-lane-v2-save-branch-incomplete={failure.kind} className="rounded border border-ma-warn-line px-2.5 py-2 text-[11px] text-ma-warn-text">
        <div className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {copy}
        </div>
        {slotFree && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-1.5 rounded border border-border px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted/50"
          >
            {failure.kind === "busy" ? "Try again" : "Save again"}
          </button>
        )}
      </div>
    );
  }
  if (failure.kind === "replayed") {
    return (
      <div data-lane-v2-save-branch-replayed="" className="rounded border border-ma-warn-line px-2.5 py-2 text-[11px] text-ma-warn-text">
        <div className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          The branch reported this save as already applied in an unexpected way.
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="mt-1.5 rounded border border-border px-2 py-0.5 text-[11px] font-medium text-foreground hover:bg-muted/50"
        >
          Try again
        </button>
      </div>
    );
  }
  if (failure.kind === "rejected") {
    return (
      <div data-lane-v2-save-branch-rejected="" className="rounded border border-ma-err-line px-2.5 py-2 text-[11px] text-ma-err-text">
        <div className="flex items-center gap-1.5 font-medium">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Your workspace branch rejected this save.
        </div>
        {failure.body?.peer_code && <p className="mt-0.5 text-muted-foreground">{failure.body.peer_code}</p>}
      </div>
    );
  }
  return (
    <div data-lane-v2-save-branch-error="" className="rounded border border-ma-err-line px-2.5 py-2 text-[11px] text-ma-err-text">
      {failure.message}
    </div>
  );
}

/** Counts for a save result's headline: files actually written, and files a stopped save never sent (`batch_not_sent`). */
export function saveResultCounts(result: Pick<SaveBranchOut, "files">): { written: number; notSent: number; total: number } {
  const notSent = result.files.filter((f) => f.status === "batch_not_sent").length;
  const written = result.files.filter((f) => !isSkippedSaveFile(f)).length;
  return { written, notSent, total: result.files.length };
}

export function SaveBranchResultView({ result }: { result: SaveBranchOut }) {
  const { written, notSent, total } = saveResultCounts(result);
  const partial = notSent > 0;
  return (
    <div
      data-lane-v2-save-branch-result=""
      data-partial={partial ? "" : undefined}
      className={cn(
        "rounded border px-2.5 py-2 text-[11px]",
        partial ? "border-ma-warn-line text-ma-warn-text" : "border-ma-ok-line bg-ma-ok-soft text-ma-ok-text",
      )}
    >
      <p>
        {partial ? (
          <>
            {notSent} of {total} file{total === 1 ? "" : "s"} not sent; save again to send the rest. Saved v{result.version} to{" "}
            <span className="font-mono">{result.branch}</span>: {written} file{written === 1 ? "" : "s"} written.
          </>
        ) : (
          <>
            Saved v{result.version} to <span className="font-mono">{result.branch}</span>: {written} file
            {written === 1 ? "" : "s"}.
          </>
        )}
      </p>
      {result.stale_package && (
        <p className="mt-0.5 flex items-center gap-1 text-ma-warn-text">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          This read an older package than the task's current one. It may not match the latest plan.
        </p>
      )}
      <ul className="mt-1 space-y-1 text-muted-foreground">
        {result.files.map((f, i) => (
          <li key={i} className="font-mono">
            {f.path}
            {isSkippedSaveFile(f) ? (
              <span className="ml-1.5 font-sans text-ma-warn-text">skipped: {saveSkipReasonLabel(f.status, f.detail)}</span>
            ) : (
              <span className="ml-1.5 font-sans">{f.status}{f.result ? ` (${f.result})` : ""}</span>
            )}
            {f.has_conflicts === true && (
              <span data-lane-v2-save-branch-has-conflicts="" className="ml-1.5 font-sans text-ma-warn-text">{SAVE_HAS_CONFLICTS_NOTE}</span>
            )}
            {f.conflict_checked === false && (
              <span className="ml-1.5 font-sans text-ma-warn-text">not conflict-checked</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExcludedDisclosure({ groups, total }: { groups: ReturnType<typeof groupExcludedByReason>; total: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border px-3.5 py-2.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown className="h-3 w-3" aria-hidden /> : <ChevronRight className="h-3 w-3" aria-hidden />}
        {total} left out
      </button>
      {open && (
        <div className="mt-1.5 space-y-2">
          {groups.map((g) => (
            <div key={g.op}>
              <p className="text-[11px] font-medium text-muted-foreground">{outOfScopeReasonLabel(g.op)}</p>
              <ul className="mt-0.5 space-y-0.5">
                {g.items.map((e, i) => (
                  <li key={i} className="font-mono text-[11px] text-foreground">
                    {e.metadata_type} · {e.full_name}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
